import React, { Component } from "react";
import { colors } from "./theme";
import { getLanguage } from "../../../utils/utils";

const LOCALES = {
  hr: "hr-HR",
  sr: "sr-RS",
  en: "en-GB",
};

// "Your HR practices in numbers - Benchmark Analysis".
//
// Each row compares one metric against the Employer Partner average. The rows
// have different units (days, EUR, hours, %), so there is no shared axis:
// instead every bar is drawn at 50% of the track when the organization is
// exactly on the average, which puts the average on the same vertical guide for
// every row regardless of unit. This is the same scale V1 uses.
//
// `rows` is [{ label, value, average, unit }], where `label` is the sentence
// from the report query with an XXX placeholder for the organization's value.
export default class V2BenchmarkBars extends Component {
  // The report query cannot store a null in a numeric column, so two sentinel
  // values stand in for the missing cases. Same values as V1.
  //
  // Rows that come from a JSON answer (the diversity percentages) bypass that
  // query and arrive as a plain null or "" when unanswered, so those are
  // treated as missing too - otherwise XXX is replaced with nothing and the
  // sentence renders as a bare unit ("... u organizaciji: %").
  _formatValue = (value, unit) => {
    if (value === 0.5111) return "n/a";
    if (value === null || value === undefined || value === "") return "n/a";

    // On this chart a zero always means the figure was not recorded rather than
    // a measured zero - none of these metrics (time to fill, EUR per employee,
    // turnover rate) is genuinely zero for an operating organisation. Printing
    // "0 EUR" next to a 34.720 EUR average reads as a real result, so it is
    // shown as n/a instead. Includes the 0.5112 sentinel, which the report
    // query uses for "zero, and the question is not flagged notApplicable".
    if (parseFloat(value) === 0 || value === 0.5112) return "n/a";

    return this._formatNumber(value, unit);
  };

  // Money is shown as a whole amount with thousands separators - a cent-level
  // figure carries no meaning next to a benchmark average, and "34720.1 EUR"
  // reads as a raw database value rather than a currency. Everything else keeps
  // the precision it arrives with, since days, hours and percentages are
  // meaningful to one decimal.
  _formatNumber = (value, unit) => {
    const number = parseFloat(value);

    if (isNaN(number)) {
      return value;
    }

    if (unit === "EUR") {
      // Locale-aware separators: hr/sr group with a dot (34.720), en with a
      // comma (34,720). Hardcoding one would misread as a decimal in the other.
      return Math.round(number).toLocaleString(
        LOCALES[getLanguage()] || "en-GB",
      );
    }

    return number;
  };

  // The sentence arrives with an XXX placeholder where the organization's own
  // value goes, e.g. "average time to fill: <strong>XXX</strong> days".
  //
  // When the value is missing the unit is dropped with it, so the row reads
  // "...: n/a" rather than "...: n/a EUR". The unit is matched explicitly from
  // row.unit rather than by pattern, because in some labels XXX comes first and
  // the words after it are the sentence, not a unit
  // ("XXX sati uloženo u usavršavanje po zaposleniku").
  _label = (row) => {
    const value = this._formatValue(row.value, row.unit);
    const label = String(row.label || "").replace("XXX", value);

    if (value !== "n/a") {
      return label;
    }

    // Only a unit that ENDS the sentence is dropped. Where XXX comes first the
    // following word is part of the sentence and has to stay, even though it
    // matches row.unit ("XXX sati uloženo u usavršavanje po zaposleniku").
    const trailing = ["%", ` ${row.unit}`];

    for (const suffix of trailing) {
      if (row.unit && label.endsWith(`n/a</strong>${suffix}`)) {
        return label.slice(0, -suffix.length);
      }
    }

    return label;
  };

  render() {
    const { averageLabel } = this.props;

    // A row with no label has no sentence to substitute the value into -
    // question_lang.reportNumbers is missing for this language - so it would
    // render as a bare unit or an empty line.
    const rows = (this.props.rows || []).filter((row) => row && row.label);

    if (!rows.length) {
      return null;
    }

    return (
      <div className="report-v2__benchmark">
        {/* Two spans: the outer one mirrors a row's track so the stylesheet can
            find the 50% mark, the inner one is centred on it. */}
        <p className="report-v2__benchmark-legend">
          <span>
            <span>{averageLabel}</span>
          </span>
        </p>

        <div className="report-v2__benchmark-rows">
          {rows.map((row, key) => {
            const value = parseFloat(row.value) || 0;
            const average = parseFloat(row.average) || 0;
            const hasAverage =
              row.average !== null &&
              row.average !== undefined &&
              row.average !== "";
            // On the average = half the track, so the guide lines up across
            // rows. Clamped to 0-100: a value far above the average would run
            // past the track, and a negative one (a loss-making year) would
            // give a negative width that the browser renders full-width.
            const valueWidth = average
              ? Math.max(0, Math.min(100, (50 * value) / average))
              : 0;

            return (
              <div className="report-v2__benchmark-row" key={key}>
                <span
                  className="report-v2__benchmark-label"
                  dangerouslySetInnerHTML={{ __html: this._label(row) }}
                />
                <div className="report-v2__benchmark-track">
                  <div
                    className="report-v2__benchmark-bar"
                    style={{
                      width: `${valueWidth}%`,
                      background: colors.secondary,
                    }}
                  />
                  {/* No average when no other organisation answered this. */}
                  {hasAverage && (
                    <span className="report-v2__benchmark-average">
                      {this._formatNumber(row.average, row.unit)}
                      {row.unit ? ` ${row.unit}` : ""}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
